import React, { useState } from 'react'
import { useParams, useHistory } from 'react-router-dom'
import Layout from '../../components/Layout'
import { useLoad } from '../../hooks/request'
import { ANIMAL_DETAIL, ANIMAL_RECORD_LIST, ANIMAL_TYPE_LIST } from '../../urls'
import { useModal } from '../../hooks/modal'
import Card from '../../components/common/Card'
import Table from '../../components/common/Table'
import Button from '../../components/common/Button'
import AnimalRecordItem from '../../components/animals/AnimalRecordItem'
import AnimalRecordCreate from '../../components/animals/AnimalRecordCreate'
import AnimalUpdate from '../../components/animals/AnimalUpdate'
import MotherBabiesSection from '../../components/animals/MotherBabiesSection'
import { getDate } from '../../utils/date'

export default function AnimalDetail() {
    const { id } = useParams()
    const history = useHistory()
    const [page, setPage] = useState(1)

    const animal = useLoad({ url: ANIMAL_DETAIL.replace('{id}', id) })
    const records = useLoad({ url: ANIMAL_RECORD_LIST, params: { animal: id } }, [id])
    const animalTypes = useLoad({ url: ANIMAL_TYPE_LIST })

    const [showCreateRecordModal, hideCreateRecordModal] = useModal(
        <AnimalRecordCreate
            animalId={id}
            onSuccess={() => {
                hideCreateRecordModal()
                records.request()
            }}
            onCancel={() => hideCreateRecordModal()}
        />,
    )

    const [showEditModal, hideEditModal] = useModal(
        <AnimalUpdate
            item={animal.response || {}}
            animalTypes={animalTypes.response?.results || []}
            onSuccess={() => {
                hideEditModal()
                animal.request()
            }}
            onCancel={() => hideEditModal()}
        />,
    )

    const animalData = animal.response || {}

    return (
        <Layout className="has-background-light">
            <div style={{ marginBottom: '20px' }}>
                <Button
                    onClick={() => history.push('/animals')}
                    text="Орқага"
                    icon="arrow-back-outline"
                    className="is-light"
                />
            </div>

            {/* Hayvon ma'lumotlari */}
            <Card>
                <div className="columns is-mobile" style={{ marginBottom: '1rem' }}>
                    <div className="column">
                        <h2 className="title is-4">Ҳайвон маълумотлари</h2>
                    </div>
                    <div className="column is-narrow">
                        <Button
                            onClick={showEditModal}
                            text="Таҳрирлаш"
                            icon="create-outline"
                            className="is-info is-outlined"
                        />
                    </div>
                </div>

                <div className="columns">
                    <div className="column is-3">
                        {animalData.imageUrl ? (
                            <figure className="image is-square">
                                <img
                                    src={animalData.imageUrl}
                                    alt={animalData.name}
                                    style={{ objectFit: 'cover', borderRadius: '8px' }}
                                    onError={(e) => {
                                        console.error('Image failed to load:', animalData.imageUrl)
                                        e.target.style.display = 'none'
                                    }}
                                    onLoad={() => console.log('Image loaded successfully:', animalData.imageUrl)}
                                />
                            </figure>
                        ) : (
                            <div
                                style={{
                                    width: '100%',
                                    paddingTop: '100%',
                                    backgroundColor: '#f5f5f5',
                                    borderRadius: '8px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    position: 'relative'
                                }}
                            >
                                <span
                                    style={{
                                        position: 'absolute',
                                        top: '50%',
                                        left: '50%',
                                        transform: 'translate(-50%, -50%)',
                                        fontSize: '48px',
                                        color: '#999'
                                    }}
                                >
                                    📷
                                </span>
                            </div>
                        )}
                    </div>

                    <div className="column is-9">
                        <table className="table is-fullwidth is-striped">
                            <tbody>
                                <tr>
                                    <td style={{ fontWeight: 'bold', width: '200px' }}>Идентификация рақами:</td>
                                    <td><strong className="is-size-5">{animalData.identificationNumber}</strong></td>
                                </tr>
                                <tr>
                                    <td style={{ fontWeight: 'bold' }}>Жинси:</td>
                                    <td>
                                        <span className={`tag ${animalData.gender === 'male' ? 'is-info' : 'is-danger'}`}>
                                            {animalData.genderDisplay}
                                        </span>
                                    </td>
                                </tr>
                                <tr>
                                    <td style={{ fontWeight: 'bold' }}>Ёш тоифаси:</td>
                                    <td>
                                        <span className={`tag ${animalData.ageCategory === 'baby' ? 'is-warning' : 'is-info'}`}>
                                            {animalData.ageCategoryDisplay || '-'}
                                        </span>
                                    </td>
                                </tr>
                                <tr>
                                    <td style={{ fontWeight: 'bold' }}>Келиб чиқиши:</td>
                                    <td>
                                        <span className={`tag ${animalData.acquisitionType === 'born_here' ? 'is-success' : 'is-link'}`}>
                                            {animalData.acquisitionTypeDisplay || '-'}
                                        </span>
                                    </td>
                                </tr>
                                <tr>
                                    <td style={{ fontWeight: 'bold' }}>Ҳайвон тури:</td>
                                    <td>{animalData.animalTypeName || '-'}</td>
                                </tr>
                                <tr>
                                    <td style={{ fontWeight: 'bold' }}>Туғилган сана:</td>
                                    <td>{animalData.birthDate ? getDate(animalData.birthDate) : '-'}</td>
                                </tr>
                                <tr>
                                    <td style={{ fontWeight: 'bold' }}>Вазн:</td>
                                    <td>
                                        {animalData.weight ? (
                                            <span className="tag is-info is-light">
                                                {animalData.weight} кг
                                            </span>
                                        ) : '-'}
                                    </td>
                                </tr>
                                <tr>
                                    <td style={{ fontWeight: 'bold' }}>Ҳолати:</td>
                                    <td>
                                        {/* eslint-disable-next-line no-nested-ternary */}
                                        <span className={`tag ${animalData.status === 'active' ? 'is-success' : animalData.status === 'sold' ? 'is-info' : animalData.status === 'died' ? 'is-danger' : animalData.status === 'lost' ? 'is-warning' : 'is-light'}`}>
                                            {animalData.statusDisplay || '-'}
                                        </span>
                                    </td>
                                </tr>
                                {animalData.status !== 'active' && animalData.soldPrice && (
                                    <tr>
                                        <td style={{ fontWeight: 'bold' }}>Сотилган нарх:</td>
                                        <td>
                                            <strong className="has-text-success">
                                                {new Intl.NumberFormat('uz-UZ').format(animalData.soldPrice)} сўм
                                            </strong>
                                        </td>
                                    </tr>
                                )}
                                {animalData.status !== 'active' && animalData.disposalDate && (
                                    <tr>
                                        <td style={{ fontWeight: 'bold' }}>Айириш санаси:</td>
                                        <td>
                                            <span className="tag is-warning is-light">
                                                {getDate(animalData.disposalDate)}
                                            </span>
                                        </td>
                                    </tr>
                                )}
                                {animalData.status !== 'active' && animalData.disposalReason && (
                                    <tr>
                                        <td style={{ fontWeight: 'bold' }}>Айириш сабаби:</td>
                                        <td>
                                            <div className="content">
                                                <p style={{
                                                    backgroundColor: '#fff3cd',
                                                    padding: '10px',
                                                    borderRadius: '4px',
                                                    borderLeft: '4px solid #ffc107'
                                                }}>
                                                    {animalData.disposalReason}
                                                </p>
                                            </div>
                                        </td>
                                    </tr>
                                )}
                                <tr>
                                    <td style={{ fontWeight: 'bold' }}>Изоҳ:</td>
                                    <td>{animalData.notes || '-'}</td>
                                </tr>
                                <tr>
                                    <td style={{ fontWeight: 'bold' }}>Қўшилган сана:</td>
                                    <td>{animalData.createdAt ? getDate(animalData.createdAt) : '-'}</td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </div>
            </Card>

            {/* Ona va bolalar bo'limi */}
            <MotherBabiesSection animalData={animalData} />

            {/* Records tarixi */}
            <Card style={{ marginTop: '20px' }}>
                <div className="columns">
                    <div className="is-size-4 column">Кўрикларнинг тарихи</div>

                    <div className="column is-narrow">
                        <Button
                            onClick={showCreateRecordModal}
                            text="Янги ёзув қўшиш"
                            icon="add-outline"
                            className="is-pulled-right is-success is-outlined"
                        />
                    </div>
                </div>

                <Table
                    loading={records.loading}
                    items={records.response ? records.response.results : []}
                    columns={{
                        index: '№',
                        date: 'Сана',
                        weight: 'Вазн',
                        // health: 'Соғлиқ ҳолати',
                        images: 'Расмлар',
                        notes: 'Изоҳ',
                        buttons: ''
                    }}
                    totalCount={records.response ? records.response.count : 0}
                    pageSize={15}
                    activePage={page}
                    onPageChange={setPage}
                    renderItem={(item, index) => (
                        <AnimalRecordItem
                            index={index}
                            item={item}
                            key={item.id}
                            onRefresh={records.request}
                        />
                    )}
                />

                {(!records.response || records.response.results.length === 0) && !records.loading && (
                    <div className="has-text-centered" style={{ padding: '40px' }}>
                        <p className="is-size-5 has-text-grey">Ҳали ёзувлар йўқ</p>
                        <p className="has-text-grey-light">Биринчи ёзувни қўшинг</p>
                    </div>
                )}
            </Card>
        </Layout>
    )
}
